import { Box, Typography } from '@mui/material';
import { Badge } from 'framework/ui/mui-builder';
import { ProjectReviewEntity } from '../../types';
import formatDiffValue, { stripHtmlTags } from '../formatDiffValue';
import { formatEnumLabel, getEntityBadge } from '../utils';
import CommentButton from './CommentButton';
import CommentNotes from './CommentNotes';
import StaleItemsList from './StaleItemsList';

interface ReviewItemRowProps {
  comment: string;
  entity: ProjectReviewEntity;
  entityType: string;
  isCommentOpen: boolean;
  isCommentingDisabled: boolean;
  onCommentChange?: (value: string) => void;
  onCommentToggle?: () => void;
}

const ReviewItemRow = ({
  comment,
  entity,
  entityType,
  isCommentOpen,
  isCommentingDisabled,
  onCommentChange,
  onCommentToggle,
}: ReviewItemRowProps) => {
  const badge = getEntityBadge(entity);
  const fieldChanges = entity.changes.filter(change => change.property !== null);

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr auto', md: 'minmax(260px, 1fr) minmax(360px, 1.5fr) auto' },
          gap: 2,
          py: 1.25,
          alignItems: 'start',
        }}
      >
        <Box>
          <Typography variant="body1">
            {entity.code ? `${entity.code} ` : ''}
            {entity.name ? stripHtmlTags(entity.name) : formatEnumLabel(entityType)}
          </Typography>
          {badge && <Badge color={badge.color}>{badge.label}</Badge>}
        </Box>
        <Box sx={{ gridColumn: { xs: '1 / 2', md: 'auto' } }}>
          {fieldChanges.map(change => (
            <Box key={`${change.property}-${change.occurredAt}`} sx={{ mb: 1 }}>
              {change.oldValue !== null && (
                <Box
                  sx={{
                    bgcolor: 'background.error',
                    color: 'error.main',
                    px: 1.25,
                    py: 0.75,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  - {formatEnumLabel(change.property ?? '')}: {formatDiffValue(change.oldValue)}
                </Box>
              )}
              <Box
                sx={{
                  bgcolor: 'background.success',
                  color: 'success.main',
                  px: 1.25,
                  py: 0.75,
                  mt: change.oldValue !== null ? 0.5 : 0,
                  whiteSpace: 'pre-wrap',
                }}
              >
                + {formatEnumLabel(change.property ?? '')}: {formatDiffValue(change.newValue)}
              </Box>
            </Box>
          ))}
          <StaleItemsList staleItems={entity.violations} />
        </Box>
        <CommentButton disabled={isCommentingDisabled} isOpen={isCommentOpen} onClick={onCommentToggle} />
      </Box>
      {isCommentOpen && <CommentNotes disabled={isCommentingDisabled} value={comment} onChange={onCommentChange} />}
    </>
  );
};

export default ReviewItemRow;
